import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Pane,
  PaneMenu,
  IconButton,
  Button,
  Row,
  Col,
  Card,
  Loading,
  Callout,
  ConfirmationModal,
  TextField,
  Icon,
} from '@folio/stripes/components';
import { useStripes } from '@folio/stripes/core';
import { ClipCopy } from '@folio/stripes/smart-components';

import useInstance from '../../../hooks/useInstance';
import useHoldings from '../../../hooks/useHoldings';
import useItems from '../../../hooks/useItems';
import useEnhancedSafetyCheck from '../../../hooks/useEnhancedSafetyCheck';
import useEnhancedSequentialDeletion from '../../../hooks/useEnhancedSequentialDeletion';
import StatusBox from './StatusBox';
import StatusIndicator from './StatusIndicator';
import CheckResultItem from './CheckResultItem';
import DeletionProgress from './DeletionProgress';
import css from './SafeDeleteInstancePage.css';

const SafeDeleteInstancePage = () => {
  const { instanceId } = useParams();
  const history = useHistory();
  const intl = useIntl();
  const callout = useRef();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');

  // Use existing hooks to fetch instance, holdings, and items data
  const {
    instance,
    isInstanceLoading,
    isError: instanceError,
    refetch: refetchInstance,
  } = useInstance(instanceId);
  const {
    holdings,
    isHoldingsLoading,
    isError: holdingsError,
    refetch: refetchHoldings,
  } = useHoldings(instanceId);
  const { items: holdingsItems, isItemsLoading, refetchAllItems } = useItems(holdings);

  // Enhanced safety check
  const safetyAnalysis = useEnhancedSafetyCheck(
    instance,
    holdings,
    holdingsItems,
  );

  // Enhanced sequential deletion hook
  const {
    deletionState,
    performSequentialDeletion,
    resetDeletionState,
    pauseDeletion,
    resumeDeletion,
    cancelDeletion,
  } = useEnhancedSequentialDeletion();

  // Flatten items from all holdings
  const items = Object.values(holdingsItems || {}).flat();

  const isLoading = isInstanceLoading || isHoldingsLoading || isItemsLoading;
  const error = instanceError || holdingsError;


  const handleClose = () => {
    history.push('/inventory');
  };

  const handleCancel = () => {
    history.goBack();
  };

  const handleDeleteRequest = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (confirmationText !== instance?.title) {
      callout.current?.sendCallout({
        type: 'error',
        message: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.error.confirmationMismatch',
          defaultMessage:
            'The confirmation text does not match the instance title',
        }),
      });
      return;
    }

    // Close modal and start deletion process
    setShowConfirmModal(false);
    setConfirmationText('');

    try {
      await performSequentialDeletion({
        instanceId,
        holdings: holdings || [],
        items: holdingsItems || {},
        deletionType,
      });

      // Show success message but don't redirect automatically
      callout.current?.sendCallout({
        type: 'success',
        message: intl.formatMessage(
          {
            id: 'ui-plugin-inventory-templates.safeDelete.successWithIndexInfo',
            defaultMessage:
              'Instance "{title}" was successfully deleted. Please note that it may take a few moments for the search index to update.',
          },
          { title: instance?.title },
        ),
      });
    } catch (error) {
      // Error is handled by the hook and displayed in DeletionProgress
      console.error('Deletion failed:', error);
    }
  };

  const renderActionMenu = () => (
    <PaneMenu>
      <IconButton
        icon="times"
        id="close-safe-delete"
        onClick={handleCancel}
        aria-label={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.close',
          defaultMessage: 'Close',
        })}
      />
    </PaneMenu>
  );

  // Get analysis from enhanced safety check
  const {
    hasHoldings,
    hasItems,
    totalItems,
    availableItems,
    unavailableItems,
    unavailableItemsDetails,
    deletionType,
    canSafelyDelete,
    safetyMessage,
    holdingsCount,
    canShowItemDetails,
  } = safetyAnalysis;

  // Check if we're in deletion process
  const isInDeletionProcess =
    deletionState.isDeleting ||
    deletionState.error ||
    deletionState.completedOperations.length > 0;

  if (isLoading) {
    return (
      <Pane
        defaultWidth="100%"
        paneTitle={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.title',
          defaultMessage: 'Safe Delete Instance',
        })}
        lastMenu={renderActionMenu()}
      >
        <Loading />
      </Pane>
    );
  }

  if (error) {
    return (
      <Pane
        defaultWidth="100%"
        paneTitle={intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.title',
          defaultMessage: 'Safe Delete Instance',
        })}
        lastMenu={renderActionMenu()}
      >
        <div className={css.errorState}>
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.error.loadFailed"
            defaultMessage="Failed to load instance data"
          />
        </div>
      </Pane>
    );
  }

  // Add handlers for deletion progress
  const handleRetryDeletion = () => {
    console.log('=== RETRY DELETION CLICKED ===');
    console.log('Current state before retry:', {
      hasInstance: !!instance,
      holdingsCount: holdings?.length,
      itemsCount: Object.values(holdingsItems || {}).flat().length,
      deletionType: safetyAnalysis?.deletionType,
      canSafelyDelete: safetyAnalysis?.canSafelyDelete,
      deletionStateInfo: {
        isDeleting: deletionState.isDeleting,
        isPaused: deletionState.isPaused,
        isCancelled: deletionState.isCancelled,
        error: deletionState.error,
        completedOperations: deletionState.completedOperations?.length,
        failedOperations: deletionState.failedOperations?.length,
      },
    });

    // Determine retry type based on state
    if (deletionState.isPaused) {
      // If paused, resume only
      console.log('Resuming paused deletion...');
      resumeDeletion();
    } else if (deletionState.error) {
      // If failed, refresh data first then start new operation
      console.log('Restarting failed deletion...');
      resetDeletionState(); // Clean state first

      // Refresh data to get current state, then start deletion
      Promise.all([
        refetchHoldings(),
        refetchAllItems()
      ]).then(([holdingsResult, itemsResult]) => {
        console.log('Data refreshed for retry:', {
          holdingsCount: holdingsResult?.data?.holdingsRecords?.length,
          itemsRefetched: itemsResult?.length,
        });

        // Use the fresh data directly from the API responses
        const freshHoldings = holdingsResult?.data?.holdingsRecords || [];
        const freshItems = {};

        // Build fresh items object from API response
        itemsResult?.forEach((queryResult) => {
          if (queryResult?.data?.items) {
            queryResult.data.items.forEach(item => {
              const holdingId = item.holdingsRecordId;
              if (!freshItems[holdingId]) {
                freshItems[holdingId] = [];
              }
              freshItems[holdingId].push(item);
            });
          }
        });

        console.log('=== DETAILED RETRY DATA ===');
        console.log('Fresh holdings from API:', freshHoldings.map(h => ({ id: h.id, updated: h.metadata?.updatedDate })));
        console.log('Fresh items built from API:', freshItems);
        console.log('Items per holding:', Object.entries(freshItems || {}).map(([holdingId, items]) => ({
          holdingId,
          itemCount: items?.length || 0,
          itemIds: items?.map(item => item.id) || []
        })));

        const allFreshItems = Object.values(freshItems).flat();
        console.log('All fresh items count:', allFreshItems.length);
        console.log('Sample fresh items:', allFreshItems.slice(0, 5).map(item => ({
          id: item.id,
          barcode: item.barcode,
          holdingId: item.holdingsRecordId
        })));

        console.log('Starting retry with fresh data:', {
          freshHoldingsCount: freshHoldings.length,
          freshItemsCount: allFreshItems.length,
          deletionType: safetyAnalysis?.deletionType,
        });
        console.log('=== END DETAILED RETRY DATA ===');

        // Start new operation with fresh data from API
        performSequentialDeletion({
          instanceId,
          holdings: freshHoldings,
          items: freshItems, // Use fresh items built from API response
          deletionType: safetyAnalysis?.deletionType || 'complete',
        }).catch(error => {
          console.error('Retry deletion failed:', error);
        });
      }).catch(error => {
        console.error('Failed to refresh data before retry:', error);
      });
    }
  };

  const handleCancelDeletion = async () => {
    console.log('=== CANCEL AND RETURN CLICKED ===');
    console.log('BEFORE: Current state before cancel:', {
      hasInstance: !!instance,
      holdingsCount: holdings?.length,
      holdingsIds: holdings?.map(h => h.id),
      itemsCount: Object.values(holdingsItems || {}).flat().length,
      isItemsLoading,
      deletionType: safetyAnalysis?.deletionType,
      canSafelyDelete: safetyAnalysis?.canSafelyDelete,
    });

    resetDeletionState();

    try {
      console.log('STEP 1: Starting refetchHoldings...');

      const refetchResult = await refetchHoldings();
      console.log('STEP 2: refetchHoldings completed:', {
        success: !!refetchResult,
        newHoldingsCount: refetchResult?.data?.holdingsRecords?.length,
        newHoldingsIds: refetchResult?.data?.holdingsRecords?.map(h => h.id),
      });

      console.log('STEP 3: Starting refetchAllItems...');

      const itemsRefetchResult = await refetchAllItems();
      console.log('STEP 4: refetchAllItems completed:', {
        success: !!itemsRefetchResult,
        refetchedQueries: itemsRefetchResult?.length,
      });

      console.log('STEP 5: Final state after refetch:', {
        holdingsCount: holdings?.length,
        holdingsIds: holdings?.map(h => h.id),
        itemsCount: Object.values(holdingsItems || {}).flat().length,
        isItemsLoading,
        deletionType: safetyAnalysis?.deletionType,
        canSafelyDelete: safetyAnalysis?.canSafelyDelete,
      });

      console.log('STEP 6: Data updated, returning to safety check');
      callout.current?.sendCallout({
        type: 'info',
        message: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.returnedToSafetyCheck',
          defaultMessage: 'Returned to safety check with updated data',
        }),
      });
    } catch (error) {
      console.error('Failed to refetch data:', error);
      callout.current?.sendCallout({
        type: 'error',
        message: intl.formatMessage({
          id: 'ui-plugin-inventory-templates.safeDelete.error.dataRefreshFailed',
          defaultMessage: 'Failed to refresh data from server',
        }),
      });
    }
  };

  return (
    <Pane
      defaultWidth="100%"
      paneTitle={
        <FormattedMessage
          id="ui-plugin-inventory-templates.safeDelete.titleWithInstance"
          defaultMessage="Safe Delete Instance - {instanceTitle}"
          values={{ instanceTitle: instance?.title || instanceId }}
        />
      }
      lastMenu={renderActionMenu()}
    >
      {/* Show deletion progress if in deletion process */}
      {isInDeletionProcess && (
        <DeletionProgress
          deletionState={deletionState}
          onRetry={handleRetryDeletion}
          onCancel={handleCancelDeletion}
          onReturnToInventory={handleClose}
          onPause={pauseDeletion}
          onResume={resumeDeletion}
          onCancelDeletion={cancelDeletion}
          instanceTitle={instance?.title}
        />
      )}

      {/* Show normal interface if not in deletion process */}
      {!isInDeletionProcess && (
        <Row>
          <Col xs={8}>
            <Card
              headerStart={
                <h4>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.safeDelete.instanceInfo"
                    defaultMessage="Instance Information"
                  />
                </h4>
              }
              cardStyle="default"
            >
              <div className={css.safeDeleteCardContent}>
                {/* Instance Information */}
                <div className={css.instanceInfoSection}>
                  <div className={css.instanceDetails}>
                    <div className={css.instanceDetailItem}>
                      <strong>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.title"
                          defaultMessage="Title:"
                        />
                      </strong>{' '}
                      {instance?.title}{' '}
                      <ClipCopy text={instance?.title || ''} />
                    </div>
                    <div className={css.instanceDetailItem}>
                      <strong>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.hrid"
                          defaultMessage="Instance HRID:"
                        />
                      </strong>{' '}
                      {instance?.hrid}
                    </div>
                    <div className={css.instanceDetailItem}>
                      <strong>
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.source"
                          defaultMessage="Source:"
                        />
                      </strong>{' '}
                      {instance?.source}
                    </div>
                  </div>
                </div>

                {/* Safety Check Results */}
                <div className={css.safetyCheckSection}>
                  <h3>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.safeDelete.safetyCheck"
                      defaultMessage="Safety Check Results"
                    />
                  </h3>

                  <div className={css.safetyCheckResults}>
                    {/* Holdings Check */}
                    <CheckResultItem
                      count={holdings?.length || 0}
                      itemType="holdings"
                      messageId="ui-plugin-inventory-templates.safeDelete.holdingsCheck"
                      defaultMessage="Holdings: {count} found"
                    />

                    {/* Items Check */}
                    <CheckResultItem
                      count={items?.length || 0}
                      itemType="items"
                      messageId="ui-plugin-inventory-templates.safeDelete.itemsCheck"
                      defaultMessage="Items: {count} found"
                      availableCount={availableItems}
                      unavailableCount={unavailableItems}
                      unavailableItems={unavailableItemsDetails}
                      showDetails
                    />
                  </div>
                </div>

                {/* Warning or Success Message */}
                <StatusBox isSuccess={canSafelyDelete} large>
                  <div>
                    <div className={css.statusMessageHeader}>
                      <StatusIndicator isSuccess={canSafelyDelete} />
                      <strong>
                        <FormattedMessage
                          id={
                            canSafelyDelete
                              ? 'ui-plugin-inventory-templates.safeDelete.canDelete'
                              : 'ui-plugin-inventory-templates.safeDelete.cannotDelete'
                          }
                          defaultMessage={
                            deletionType === 'complete'
                              ? 'Safe to Delete Completely'
                              : deletionType === 'instance-only'
                                ? 'Safe to Delete'
                                : 'Cannot Delete Instance'
                          }
                        />
                      </strong>
                    </div>
                    <div className={css.statusMessageContent}>
                      {deletionType === 'blocked' && (
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.cannotDeleteReason"
                          defaultMessage="This instance has {unavailableCount} unavailable items. All items must be available (status: Available) before deletion."
                          values={{ unavailableCount: unavailableItems }}
                        />
                      )}
                      {deletionType === 'complete' && totalItems > 0 && (
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.canDeleteCompleteReason"
                          defaultMessage="All items are available. Deletion will remove the instance, all holdings ({holdingsCount}), and all items ({totalItems})."
                          values={{
                            holdingsCount,
                            totalItems,
                          }}
                        />
                      )}
                      {deletionType === 'complete' &&
                        totalItems === 0 &&
                        holdingsCount > 0 && (
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.safeDelete.canDeleteHoldingsOnlyReason"
                            defaultMessage="This instance has holdings with no items. Deletion will remove the instance and all holdings ({holdingsCount})."
                            values={{
                              holdingsCount,
                            }}
                          />
                      )}
                      {deletionType === 'instance-only' && (
                        <FormattedMessage
                          id="ui-plugin-inventory-templates.safeDelete.canDeleteInstanceOnlyReason"
                          defaultMessage="This instance has no associated holdings or items."
                        />
                      )}
                    </div>
                  </div>
                </StatusBox>
              </div>
            </Card>
          </Col>

          <Col xs={4}>
            <Card
              headerStart={
                <h4>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.safeDelete.actions"
                    defaultMessage="Actions"
                  />
                </h4>
              }
            >
              <div className={css.actionsCardContent}>
                <Button
                  buttonStyle="primary"
                  fullWidth
                  disabled={!canSafelyDelete || deletionState.isDeleting}
                  onClick={handleDeleteRequest}
                  loading={deletionState.isDeleting}
                  className={css.deleteButton}
                >
                  <Icon
                    icon="trash"
                    size="small"
                    style={{ marginRight: '8px' }}
                  />
                  <FormattedMessage
                    id={
                      deletionType === 'complete'
                        ? 'ui-plugin-inventory-templates.safeDelete.deleteCompleteButton'
                        : 'ui-plugin-inventory-templates.safeDelete.deleteButton'
                    }
                    defaultMessage={
                      deletionType === 'complete'
                        ? 'Delete Completely'
                        : 'Delete Instance'
                    }
                  />
                </Button>

                <Button
                  buttonStyle="default"
                  fullWidth
                  onClick={handleCancel}
                  className={css.cancelButton}
                >
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.safeDelete.cancelButton"
                    defaultMessage="Cancel"
                  />
                </Button>

                {/* Help Section */}
                <div className={css.helpSection}>
                  <div className={css.helpSectionHeader}>
                    <Icon icon="info" size="small" className={css.helpIcon} />
                    <strong>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.safeDelete.helpTitle"
                        defaultMessage="Help"
                      />
                    </strong>
                  </div>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.safeDelete.helpText"
                    defaultMessage="Safe deletion works for instances with no holdings/items and instances where all items have 'Available' status. This prevents accidental data loss."
                  />
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmModal}
        heading={
          <FormattedMessage
            id={
              deletionType === 'complete'
                ? 'ui-plugin-inventory-templates.safeDelete.confirmModal.titleComplete'
                : 'ui-plugin-inventory-templates.safeDelete.confirmModal.title'
            }
            defaultMessage={
              deletionType === 'complete'
                ? 'Confirm Complete Deletion'
                : 'Confirm Instance Deletion'
            }
          />
        }
        message={
          <div>
            {deletionType === 'complete' && (
              <div className={css.warningSection}>
                <p style={{ fontWeight: 'bold', color: '#ff4d4f' }}>
                  <FormattedMessage
                    id="ui-plugin-inventory-templates.safeDelete.confirmModal.warningComplete"
                    defaultMessage="⚠️ WARNING: This will permanently delete:"
                  />
                </p>
                <ul>
                  <li>
                    <FormattedMessage
                      id="ui-plugin-inventory-templates.safeDelete.confirmModal.instanceLine"
                      defaultMessage="• Instance: {title}"
                      values={{ title: instance?.title }}
                    />
                  </li>
                  {holdingsCount > 0 && (
                    <li>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.safeDelete.confirmModal.holdingsLine"
                        defaultMessage="• {count} Holdings records"
                        values={{ count: holdingsCount }}
                      />
                    </li>
                  )}
                  {totalItems > 0 && (
                    <li>
                      <FormattedMessage
                        id="ui-plugin-inventory-templates.safeDelete.confirmModal.itemsLine"
                        defaultMessage="• {count} Items (all available)"
                        values={{ count: totalItems }}
                      />
                    </li>
                  )}
                </ul>
              </div>
            )}

            <p>
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.confirmModal.message"
                defaultMessage="This action cannot be undone."
              />
            </p>
            <p>
              <FormattedMessage
                id="ui-plugin-inventory-templates.safeDelete.confirmModal.instruction"
                defaultMessage="Please type the instance title to confirm:"
              />
            </p>
            <TextField
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={instance?.title}
              fullWidth
              autoFocus
            />
          </div>
        }
        confirmLabel={
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.confirmModal.deleteButton"
            defaultMessage="Delete Instance"
          />
        }
        cancelLabel={
          <FormattedMessage
            id="ui-plugin-inventory-templates.safeDelete.confirmModal.cancelButton"
            defaultMessage="Cancel"
          />
        }
        onCancel={() => {
          setShowConfirmModal(false);
          setConfirmationText('');
        }}
        onConfirm={handleConfirmDelete}
        isConfirmButtonDisabled={confirmationText !== instance?.title}
      />

      <Callout ref={callout} />
    </Pane>
  );
};

SafeDeleteInstancePage.propTypes = {};

export default SafeDeleteInstancePage;
